import type { ViewerDraftSaveStatus } from "../../../types/viewerDeployment";
import {
  UnauthorizedViewerRequestError,
  ViewerRequestError,
} from "../../../services/adminApiClient";

export interface ViewerDraftSyncState {
  revision: number;
  saveStatus: ViewerDraftSaveStatus;
  conflictMessage: string | null;
  errorMessage: string | null;
}

const DRAFT_CONFLICT_MESSAGE =
  "Draft changed on the server. Reload before creating a preview or saving again.";
const SESSION_EXPIRED_MESSAGE = "Session expired. Please sign in again.";
const FALLBACK_SAVE_ERROR_MESSAGE = "Failed to save viewer draft.";

export const INITIAL_VIEWER_DRAFT_SYNC_STATE: ViewerDraftSyncState = {
  revision: 0,
  saveStatus: "idle",
  conflictMessage: null,
  errorMessage: null,
};

export function buildViewerDraftSyncIdleState(
  revision = 0,
): ViewerDraftSyncState {
  return {
    revision,
    saveStatus: "idle",
    conflictMessage: null,
    errorMessage: null,
  };
}

export function buildViewerDraftSyncSavingState(
  revision: number,
): ViewerDraftSyncState {
  return {
    revision,
    saveStatus: "saving",
    conflictMessage: null,
    errorMessage: null,
  };
}

export function buildViewerDraftSyncSavedState(
  revision: number,
): ViewerDraftSyncState {
  return {
    revision,
    saveStatus: "saved",
    conflictMessage: null,
    errorMessage: null,
  };
}

export function resolveViewerDraftSyncFailureState(params: {
  revision: number;
  error: unknown;
}): ViewerDraftSyncState {
  const { revision, error } = params;
  if (error instanceof ViewerRequestError && error.code === "DRAFT_CONFLICT") {
    return {
      revision,
      saveStatus: "conflict",
      conflictMessage: DRAFT_CONFLICT_MESSAGE,
      errorMessage: null,
    };
  }

  return {
    revision,
    saveStatus: "failed",
    conflictMessage: null,
    errorMessage:
      error instanceof UnauthorizedViewerRequestError
        ? SESSION_EXPIRED_MESSAGE
        : error instanceof Error
          ? error.message
          : FALLBACK_SAVE_ERROR_MESSAGE,
  };
}
